/**
 * First-touch attribution.
 *
 * Captured once per browser session and never overwritten afterwards, so a
 * visitor who lands from an ad and submits three pages later still carries the
 * original campaign data. Only non-personal navigation data is stored.
 */
const KEY = "catuik_attribution_v1";

export type Attribution = {
  landing_page_path: string;
  referrer: string;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  gclid: string | null;
  fbclid: string | null;
  msclkid: string | null;
  captured_at: string;
};

const PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gclid",
  "fbclid",
  "msclkid",
] as const;

function read(): Attribution | null {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Attribution) : null;
  } catch {
    return null;
  }
}

function isExternalReferrer(referrer: string) {
  if (!referrer) return false;
  try {
    return new URL(referrer).host !== window.location.host;
  } catch {
    return false;
  }
}

/** Reads the stored first-touch data, capturing it on the first call of a session. */
export function getAttribution(): Attribution | null {
  if (typeof window === "undefined") return null;
  const existing = read();
  if (existing) return existing;

  const params = new URLSearchParams(window.location.search);
  const next: Attribution = {
    landing_page_path: window.location.pathname + window.location.search,
    referrer: isExternalReferrer(document.referrer) ? document.referrer : "",
    captured_at: new Date().toISOString(),
    ...(Object.fromEntries(PARAMS.map((p) => [p, params.get(p)])) as Pick<
      Attribution,
      (typeof PARAMS)[number]
    >),
  };

  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* storage unavailable — attribution is best-effort */
  }
  return next;
}
